import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Public viewer for a shared Raw Footage clip. No login, no auth header.
//
// Deliberately a SEPARATE page from ShareLinkViewer rather than a mode flag on
// it. That viewer is a 75/25 two-column layout whose entire right column is the
// comment panel, plus guest sessions, a guest login modal, comment-marker
// plumbing through VideoPlayer, and a top bar that shows the client name. A
// shared footage clip must expose the clip and nothing else — no comments, no
// client name — so a flag would have switched off nearly all of it while
// leaving both call sites conditional. Footage also needs two things that
// viewer does not have: an <audio> branch, and an "archived" state.
//
// What this page can show is bounded by what the API returns: file name, a
// playable URL, and whether download is allowed. There is no transcript, match
// data, client name or note in the payload to leak.

const API_BASE = process.env.REACT_APP_API_URL || '';

const FULL_CENTER = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#07070c', fontFamily: 'var(--font)',
};

function Message({ title, body }) {
  return (
    <div style={FULL_CENTER}>
      <div style={{ textAlign: 'center', padding: '0 32px', maxWidth: 460 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-primary)', marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--tx-faint)', lineHeight: 1.6 }}>{body}</div>
      </div>
    </div>
  );
}

export default function FootageShareViewer() {
  const { token } = useParams();
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [inactive,   setInactive]   = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/share/footage/${token}`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled && err.response?.status === 404) setInactive(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/share/footage/${token}/download`);
      window.open(res.data.url, '_blank');
    } catch (err) {
      alert(err?.response?.data?.error || 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  }

  if (loading) {
    return (
      <div style={FULL_CENTER}>
        <span style={{ fontSize: 13, color: 'var(--tx-faint)' }}>Loading…</span>
      </div>
    );
  }

  if (inactive || !data) {
    return (
      <Message
        title="This link is no longer active."
        body="The share link you followed has been deactivated or has expired."
      />
    );
  }

  // The clip was archived to cold storage after this link was made. Its file is
  // genuinely not there, so say so rather than rendering a player that would
  // spin forever against a URL the CDN will refuse.
  if (data.archived) {
    return (
      <Message
        title="This clip has been archived."
        body={data.message || 'This clip is no longer available at this link.'}
      />
    );
  }

  const { clip, shareLink } = data;
  const isAudio = clip.mediaKind === 'audio';

  return (
    <div style={{ background: '#07070c', minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font)', color: 'var(--tx-primary)' }}>
      {/* Top bar — file name only. No client name, by design. */}
      <div style={{
        height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(7,7,12,0.92)', backdropFilter: 'blur(8px)',
      }}>
        <div style={{ fontSize: 12, color: 'var(--tx-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {clip.fileName}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {shareLink.allowDownload && (
            <button className="btn-ghost" style={{ fontSize: 12 }} disabled={downloading} onClick={handleDownload}>
              {downloading ? 'Preparing…' : '↓ Download'}
            </button>
          )}
          <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontWeight: 600, opacity: 0.5 }}>Heliconflow</span>
        </div>
      </div>

      {/* Player. Audio is browser-playable as uploaded and has no frame, so it
          gets a native <audio> element rather than a video box with a blank poster. */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: 0 }}>
        {isAudio ? (
          <div style={{ width: '100%', maxWidth: 560, textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 72, height: 72, borderRadius: 12, marginBottom: 18,
              background: 'rgba(139,109,255,0.12)', border: '1px solid rgba(139,109,255,0.25)',
              fontSize: 13, fontWeight: 700, letterSpacing: 1, color: 'var(--violet)',
            }}>
              {(clip.fileName.match(/\.([A-Za-z0-9]{1,6})$/)?.[1] || 'AUDIO').toUpperCase()}
            </div>
            <audio controls preload="metadata" style={{ width: '100%' }}>
              <source src={clip.playbackUrl} />
              Your browser doesn't support inline audio playback.
            </audio>
          </div>
        ) : (
          <video
            controls
            preload="metadata"
            poster={clip.thumbnailUrl || undefined}
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8, background: '#000' }}
          >
            <source src={clip.playbackUrl} />
            Your browser doesn't support inline video playback.
          </video>
        )}
      </div>
    </div>
  );
}
